/**
 * Conversion-engine voucher helpers for the redemption path (Stage 1).
 *
 * A voucher is "conversion-engine bound" only when `conversion_engine_id` is set.
 * Those vouchers start INACTIVE (code-only, no value) and gain a redeemable value
 * (`current_value`) as catalysts fire at check-in. LEGACY vouchers (no engine id)
 * are completely unaffected — `/redeem` keeps its current behavior for them
 * (audit guardrail #1).
 *
 * Pure functions so the gate + redeemable-amount logic are unit-tested directly.
 */

export interface ConversionVoucherFields {
  conversion_engine_id?: string | null;
  activation_state?: string | null;
  current_value?: number | null;
  discount_value: number;
  /**
   * Event-ticket reward state (Phase A). A pre-minted ticket reward voucher is a $0 placeholder until an
   * offer/value is assigned: `pending` = not redeemable yet, `assigned` = redeemable (value in
   * `discount_value`), `void` = cancelled. NULL on every non-ticket voucher (unaffected).
   */
  reward_state?: string | null;
}

/** True when the voucher is governed by the conversion engine. */
export function isConversionVoucher(v: {
  conversion_engine_id?: string | null;
}): boolean {
  return Boolean(v.conversion_engine_id);
}

export const CONVERSION_NOT_ACTIVE_MESSAGE =
  "This voucher isn't active yet. It activates at check-in once the reward conditions are met.";

export const REWARD_PENDING_MESSAGE =
  "Your reward is being prepared — please check back soon.";

export const REWARD_VOID_MESSAGE =
  "This reward is no longer available. Please see a USLS representative.";

/**
 * Reason a redemption should be blocked, or null if allowed. Legacy vouchers are
 * never blocked here (they fall through to the existing checks). A ticket-reward
 * placeholder is blocked until its value is `assigned`; a conversion voucher is
 * blocked until its `activation_state` is `'active'`.
 */
export function conversionRedeemBlockReason(
  v: ConversionVoucherFields,
): string | null {
  // Event-ticket reward placeholder: not redeemable until an offer/value is assigned (Phase A). Checked
  // first because a ticket reward is a NON-conversion voucher and would otherwise fall through to "allowed".
  if (v.reward_state === "pending") {
    return REWARD_PENDING_MESSAGE;
  }
  if (v.reward_state === "void") {
    return REWARD_VOID_MESSAGE;
  }
  if (!isConversionVoucher(v)) {
    return null;
  }
  return v.activation_state === "active" ? null : CONVERSION_NOT_ACTIVE_MESSAGE;
}

/**
 * The redeemable dollar amount for a voucher. Conversion vouchers redeem against
 * their escalated `current_value` (the ledger projection); legacy vouchers redeem
 * against `discount_value` as before (guardrail: don't reuse discount_value as the
 * conversion value).
 */
export function redeemableValue(v: ConversionVoucherFields): number {
  if (isConversionVoucher(v)) {
    return v.current_value ?? 0;
  }
  return v.discount_value;
}
